import React, { createContext, useCallback, useContext, useState } from "react";

const UIContext = createContext(null);

// useUI().run(fn, success) — shows a blocking spinner overlay while fn() runs,
// then a success toast (string, or a function of fn's result) on success, or an
// error toast on failure. Returns fn's result (or undefined on error; never throws).
// useUI().toast(message, type) — fire a toast directly.
export const useUI = () => useContext(UIContext);

// "simple" shows just goal + budget; "advanced" shows every stage, plan, ledger and log.
const MODE_KEY = "instamatch_mode";
const loadMode = () => {
  try {
    return localStorage.getItem(MODE_KEY) === "advanced" ? "advanced" : "simple";
  } catch {
    return "simple";
  }
};

let _seq = 0;

export function UIProvider({ children }) {
  const [pending, setPending] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [mode, setModeState] = useState(loadMode);
  const setMode = useCallback((m) => {
    setModeState(m);
    try {
      localStorage.setItem(MODE_KEY, m);
    } catch {
      /* private mode */
    }
  }, []);

  const dismiss = useCallback((id) => setToasts((t) => t.filter((x) => x.id !== id)), []);

  const toast = useCallback(
    (message, type = "success") => {
      const id = ++_seq;
      setToasts((t) => [...t, { id, message, type }]);
      setTimeout(() => dismiss(id), 3800);
    },
    [dismiss]
  );

  const run = useCallback(
    async (fn, success) => {
      setPending((n) => n + 1);
      try {
        const result = await fn();
        if (success) {
          const text = typeof success === "function" ? success(result) : success;
          if (text) toast(text, "success");
        }
        return result;
      } catch (e) {
        toast(e?.response?.data?.detail || e?.message || "Something went wrong", "error");
        return undefined;
      } finally {
        setPending((n) => n - 1);
      }
    },
    [toast]
  );

  return (
    <UIContext.Provider value={{ run, toast, mode, setMode, advanced: mode === "advanced" }}>
      {children}

      {pending > 0 && (
        <div className="overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="spinner" />
        </div>
      )}

      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`} onClick={() => dismiss(t.id)}>
            <span className="toast-icon">{t.type === "error" ? "✕" : "✓"}</span>
            <span className="toast-msg">{t.message}</span>
          </div>
        ))}
      </div>
    </UIContext.Provider>
  );
}
